import { useEffect, useRef, useState, type ReactElement } from 'react';

import type { FeedbackReason } from '@harness/protocol';

const REASONS: { value: FeedbackReason; label: string }[] = [
  { value: 'harmful', label: 'Harmful content' },
  { value: 'inaccurate', label: 'Inaccurate' },
  { value: 'off_topic', label: 'Off topic' },
  { value: 'privacy', label: 'Privacy concern' },
  { value: 'other', label: 'Something else' },
];

interface Acknowledgement {
  reportId: string;
  acknowledgedAt: string;
}

/**
 * The report flow, against the five published reasons, in a native dialog so
 * focus trapping and escape come from the platform rather than from a
 * reimplementation of it. On submit the returned report id is shown in the
 * mono face, because it is an identifier the reader may want to quote.
 */
export function FeedbackDialog({
  streamId,
  open,
  onClose,
}: {
  streamId: string | null;
  open: boolean;
  onClose: () => void;
}): ReactElement {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [reason, setReason] = useState<FeedbackReason>('inaccurate');
  const [result, setResult] = useState<Acknowledgement | null>(null);
  const [failed, setFailed] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;
    if (open && !dialog.open) {
      setResult(null);
      setFailed(false);
      dialog.showModal();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const submit = (): void => {
    setSending(true);
    setFailed(false);
    fetch('/v1/feedback/report', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ reason, ...(streamId === null ? {} : { streamId }) }),
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`report answered ${String(response.status)}`);
        setResult((await response.json()) as Acknowledgement);
      })
      .catch(() => {
        setFailed(true);
      })
      .finally(() => {
        setSending(false);
      });
  };

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose} aria-labelledby="feedback-title">
      {result === null ? (
        <form
          method="dialog"
          className="dialog__form"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <h3 id="feedback-title">Report this response</h3>
          <fieldset className="dialog__reasons">
            <legend className="muted">What is wrong with it</legend>
            {REASONS.map((entry) => (
              <label key={entry.value} className="dialog__reason">
                <input
                  type="radio"
                  name="reason"
                  value={entry.value}
                  checked={reason === entry.value}
                  onChange={() => {
                    setReason(entry.value);
                  }}
                />
                <span>{entry.label}</span>
                <span className="mono muted">{entry.value}</span>
              </label>
            ))}
          </fieldset>
          {failed && (
            <p className="dialog__error" role="alert">
              The report was not sent. Retry, or close and try later.
            </p>
          )}
          <div className="dialog__actions">
            <button type="button" className="button button--quiet" onClick={onClose}>
              Close
            </button>
            <button type="submit" className="button button--primary" disabled={sending}>
              Send report
            </button>
          </div>
        </form>
      ) : (
        <div className="dialog__form">
          <h3 id="feedback-title">Report received</h3>
          <p>
            Reference <span className="mono">{result.reportId}</span>, acknowledged at{' '}
            <span className="mono">{new Date(result.acknowledgedAt).toLocaleTimeString()}</span>.
          </p>
          <div className="dialog__actions">
            <button type="button" className="button button--primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      )}
    </dialog>
  );
}
